import { CalendarPlus, ChartLine, ChevronRight, MapPinHouse } from 'lucide-react';
import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Head } from '@/components/seo/Head/Head';
import { PageHeading } from '@/components/shared/PageHeading/PageHeading';
import { Button } from '@/components/ui/Button/Button';
import { paths } from '@/config/paths';
import { CancelDialog } from '@/features/scheduling/components/CancelDialog/CancelDialog';
import { LastSessionOverview } from '@/features/scheduling/components/LastSessionOverview/LastSessionOverview';
import { ManageSessionProvider } from '@/features/scheduling/components/ManageSessionProvider/ManageSessionProvider';
import { RescheduleDialog } from '@/features/scheduling/components/RescheduleDialog/RescheduleDialog';
import { ScheduleDialog } from '@/features/scheduling/components/ScheduleDialog/ScheduleDialog';
import { InteractiveDemoCard } from '@/features/home/components/InteractiveDemoCard/InteractiveDemoCard';
import { UpcomingSessionsOverview } from '@/features/scheduling/components/UpcomingSessionsOverview/UpcomingSessionsOverview';
import { SchedulingBlockedTooltip } from '@/features/scheduling/components/SchedulingPaused/SchedulingBlockedTooltip';
import { useCanSchedule } from '@/features/scheduling/hooks/useCanSchedule';
import { useIsSchedulingBlocked } from '@/features/systemBanner/hooks/useIsSchedulingBlocked';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { captureAnalyticsEvent } from '@/lib/mixpanel';
import { useAuthStore } from '@/stores/authStore';
import { HomeHeadingBox } from '../../../features/home/components/HomeHeadingBox/HomeHeadingBox';

export const HomeRoute = () => {
  const { t } = useTranslation('home', { keyPrefix: 'home' });

  const isTablet = useMediaQuery('tablet');

  const { userData } = useAuthStore();
  const isSchedulingEnabled = !!userData?.center?.features?.scheduling;
  const externalSchedulingUrl = userData?.center?.externalSchedulingUrl;
  const hasExternalScheduling = isSchedulingEnabled && !!externalSchedulingUrl;
  const { canSchedule } = useCanSchedule();
  const isSchedulingBlocked = useIsSchedulingBlocked();
  const isSchedulingDisplayed = canSchedule;
  const isDemoDisplayed = !!userData?.isDemoEligible;

  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const openButtonRef = useRef<HTMLButtonElement>(null);

  const onScheduleClick = () => {
    if (isSchedulingBlocked) return;

    void captureAnalyticsEvent('scheduling_started');

    if (hasExternalScheduling) {
      window.open(externalSchedulingUrl, '_blank', 'noopener,noreferrer');
    } else {
      setIsScheduleDialogOpen(true);
    }
  };

  return (
    <>
      <Head title='Home' />
      <ScheduleDialog isOpened={isScheduleDialogOpen} onOpenChange={setIsScheduleDialogOpen} openButtonRef={openButtonRef} />

      <PageHeading as='div' containerClassName='flex' className='max-tablet:flex-col tablet:items-center flex flex-1 justify-between gap-4'>
        <h1>{t('title')}</h1>
        {!isTablet && isSchedulingDisplayed && (
          <SchedulingBlockedTooltip isBlocked={isSchedulingBlocked} className='w-full'>
            <Button className='w-full whitespace-normal' onClick={onScheduleClick} ref={openButtonRef} disabled={isSchedulingBlocked}>
              <CalendarPlus className='text-text-default-inverted size-5' />
              {t('scheduleSessionButton.label')}
            </Button>
          </SchedulingBlockedTooltip>
        )}
      </PageHeading>

      <div className='flex flex-col gap-10'>
        <div className='desktop:gap-6 flex flex-wrap gap-4'>
          <HomeHeadingBox onScheduleClick={onScheduleClick} openButtonRef={openButtonRef} />
          <div className='desktop:gap-6 flex flex-[40] flex-col gap-4'>
            {isDemoDisplayed && <InteractiveDemoCard />}
            <Button variant='link' className='bg-fill-background-light h-full justify-between gap-3 rounded-xl px-5 py-7' asChild>
              <Link to={paths.progressCenter.home.path}>
                <div className='flex items-center gap-3'>
                  <ChartLine className='text-text-brand-primary size-6' />
                  <span className='text-body-m-semibold'>{t('progressCenterButton.label')}</span>
                </div>
                <ChevronRight className='text-text-default-secondary size-6' />
              </Link>
            </Button>
            <Button variant='link' className='bg-fill-background-light h-full justify-between gap-3 rounded-xl px-5 py-7' asChild>
              <Link to={paths.learningCenter.path}>
                <div className='flex items-center gap-3'>
                  <MapPinHouse className='text-text-brand-primary size-6' />
                  <span className='text-body-m-semibold'>{t('centerButton.label')}</span>
                </div>
                <ChevronRight className='text-text-default-secondary size-6' />
              </Link>
            </Button>
          </div>
        </div>

        <ManageSessionProvider>
          <RescheduleDialog />
          <CancelDialog />
          <div className='flex flex-col gap-6'>
            {isSchedulingEnabled && !hasExternalScheduling && <UpcomingSessionsOverview />}
            <LastSessionOverview />
          </div>
        </ManageSessionProvider>
      </div>
    </>
  );
};
